<!-- SORT IT — five things, the right order, a clock that never stops.

     UP AND DOWN BUTTONS, NOT A DRAG HANDLE. A real drag needs pointer capture
     that behaves differently inside a WKWebView, is impossible with switch
     control or a screen reader, and is *slower* than tapping for a list of
     five. Same interaction, none of the costs.

     Five puzzles, forty-five seconds each. Running out submits whatever order
     he has — partial credit rather than nothing, because the clock should
     hurry him up, not wipe the work. -->
<template>
  <IonPage>
    <AppHeader title="Sort It" back="/tabs/play" />

    <IonContent :fullscreen="true">
      <template v-if="!started">
        <div class="pad" style="padding-top: var(--sp-6)">
          <p class="ex-display lg">Sort It</p>
          <p class="ex-sub">
            Five things, one right order. Nudge them up and down and lock it in
            before the clock goes. You get points for every one in the right
            place, so a near miss still counts.
          </p>
        </div>
        <ModeRecord mode="sort-it" />

        <div class="pad" style="margin-top: var(--sp-5)">
          <IonButton expand="block" size="large" @click="begin">Start sorting</IonButton>
        </div>
      </template>

      <template v-else-if="current">
        <GameHud :score="score" :streak="streak" label="Puzzle" :value="`${idx + 1}/${ROUNDS}`" />
        <div v-if="timed" class="clock-bar" :class="clockClass" aria-hidden="true"><i :style="{ width: clockPct + '%' }" /></div>

        <div class="q-stage" ref="stageRef" tabindex="-1" style="padding-bottom: var(--sp-3)">
          <span class="q-topic">
            <span aria-hidden="true">{{ topicEmoji(current.topic) }}</span>
            {{ topicLabel(current.topic) }}
          </span>
          <p class="q-prompt">{{ current.prompt }}</p>
        </div>

        <div class="sort-ends"><span>1. {{ current.lowLabel }}</span></div>

        <div class="sort-list">
          <div
            v-for="(item, i) in order"
            :key="item"
            class="sort-row"
            :class="rowClass(item, i)"
          >
            <!-- The position, shown. Without it the only cue to where a thing
                 sits is its place on screen, which is no cue at all once the
                 list has been shuffled twice and he has lost his place. -->
            <span class="rank" aria-hidden="true">{{ i + 1 }}</span>
            <span class="label">
              {{ item }}
              <span v-if="checked" class="sr-only">
                {{ rowClass(item, i) === 'right' ? '— correct spot' : `— wrong spot, this should be ${current.ordered[i]}` }}
              </span>
            </span>
            <span class="nudge">
              <button
                type="button"
                :disabled="i === 0 || checked"
                :aria-label="`Move ${item} up, currently ${i + 1} of ${order.length}`"
                @click="move(i, -1)"
              >&uarr;</button>
              <button
                type="button"
                :disabled="i === order.length - 1 || checked"
                :aria-label="`Move ${item} down, currently ${i + 1} of ${order.length}`"
                @click="move(i, 1)"
              >&darr;</button>
            </span>
          </div>
        </div>

        <div class="sort-ends">
          <span style="margin-left:auto">{{ order.length }}. {{ current.highLabel }}</span>
        </div>

        <div class="pad" style="margin-top: var(--sp-4)">
          <IonButton expand="block" size="large" :disabled="checked" @click="check()">
            Lock it in
          </IonButton>
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import ModeRecord from '../components/ModeRecord.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import { topicEmoji, topicLabel } from '../game/topics.js';
import { drawQuestions, pointsFor, shuffle } from '../game/engine.js';
import { drawOptions } from '../game/profile.js';
import { useCountdown } from '../game/clock.js';
import { paced, timed } from '../game/pace.js';
import { finishRun } from '../game/run.js';
import { useAbandonGuard, useStopOnLeave, useResetOnEnter } from '../game/guard.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const ROUNDS = 5;
const PER_PUZZLE_MS = 45000;

const started = ref(false);
const over = ref(false);
const queue = ref([]);
const idx = ref(0);
const order = ref([]);
const checked = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const verdict = ref({ text: '', why: '', good: true });

const clock = useCountdown(paced(PER_PUZZLE_MS), () => check(true));
const current = computed(() => queue.value[idx.value] ?? null);
const clockPct = computed(() => (clock.remaining.value / paced(PER_PUZZLE_MS)) * 100);
const clockClass = computed(() => {
  const s = clock.remaining.value / 1000;
  return s <= 8 ? 'panic' : s <= 18 ? 'warn' : '';
});

useAbandonGuard(
  computed(() => started.value && !over.value),
  {
    message: 'You are part-way through the five puzzles. Leaving now loses this score.',
    // Confirming used only to PERMIT the navigation — the run carried on in
    // the background. "Give up" now actually gives up.
    onAbandon: () => teardown(),
  },
);

let advanceTimer = null;

function deal() {
  checked.value = false;
  const right = current.value?.ordered ?? [];
  // Shuffle until it is not already the answer — a one-in-120 chance of
  // handing him a solved puzzle is small, and would look exactly like a bug.
  let next = shuffle(right);
  let guard = 0;
  while (guard < 8 && next.every((v, i) => v === right[i])) {
    next = shuffle(right);
    guard += 1;
  }
  order.value = next;
  clock.reset(paced(PER_PUZZLE_MS));
  if (timed.value) clock.start();
  // Each puzzle is its own deliberate task, not a rapid-fire question, so
  // moving focus to it (rather than just announcing) is worth the jump —
  // without it the Start button or "Lock it in" from the last puzzle is all
  // a keyboard or screen-reader user has left to stand on.
  nextTick(() => stageRef.value?.focus());
}

function begin() {
  queue.value = drawQuestions('sort-it', { ...drawOptions.value, count: ROUNDS });
  started.value = true;
  idx.value = 0;
  deal();
}

function move(i, delta) {
  if (checked.value) return;
  const j = i + delta;
  if (j < 0 || j >= order.value.length) return;
  const next = [...order.value];
  [next[i], next[j]] = [next[j], next[i]];
  order.value = next;
  haptic('selection');
  // The list reorders visually and says nothing. Without this a screen reader
  // user taps Up and gets silence, with no way to tell whether it worked.
  announce(`${next[j]} moved to ${j + 1} of ${next.length}`);
}

function rowClass(item, i) {
  if (!checked.value) return '';
  return current.value.ordered[i] === item ? 'right' : 'wrong';
}

function check(ranOut = false) {
  if (checked.value || over.value || !current.value) return;
  clock.stop();
  checked.value = true;

  const right = current.value.ordered;
  const hits = order.value.filter((v, i) => v === right[i]).length;
  const perfect = hits === right.length;

  // Partial credit, per position. A clock that wipes the work would make the
  // last ten seconds pointless rather than tense.
  score.value += pointsFor({
    difficulty: current.value.difficulty,
    streak: streak.value,
    multiplier: hits / right.length,
  });

  if (perfect) {
    correct.value += 1;
    streak.value += 1;
    bestChain.value = Math.max(bestChain.value, streak.value);
    haptic('success');
    verdict.value = { text: pun('correct'), why: current.value.reason, good: true };
  } else {
    wrong.value += 1;
    streak.value = 0;
    haptic(ranOut ? 'warning' : 'error');
    verdict.value = {
      text: ranOut ? pun('clock') : `${hits} out of ${right.length} in the right spot.`,
      why: current.value.reason,
      good: false,
    };
  }

  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(next, 2600);
}

function next() {
  if (over.value) return;
  verdict.value = { text: '', why: '', good: true };
  idx.value += 1;
  if (idx.value >= Math.min(ROUNDS, queue.value.length)) {
    end();
    return;
  }
  deal();
}

async function end() {
  if (over.value) return;
  over.value = true;
  clock.stop();
  clearTimeout(advanceTimer);
  await finishRun({
    mode: 'sort-it',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: 0,
  });
  router.replace('/tabs/play/results');
}

// Everything this screen has running, stopped. Idempotent: it is called on
// every leave, on abandon, and again before every re-entry.
function teardown() {
  over.value = true;
  clock.stop();
  clearTimeout(advanceTimer);
}

// Back to the pre-game screen. Without this, "Go again" returned to this same
// cached instance still holding the finished run.
function resetRun() {
  teardown();
  over.value = false;
  // Every ref back to the literal it was declared with — the state a
  // fresh mount would have. Resetting only the run flags left the LAST
  // question's verdict and its revealed answer painted over the new
  // board, so a new game opened showing why the previous one ended.
  started.value = false;
  over.value = false;
  queue.value = [];
  idx.value = 0;
  order.value = [];
  checked.value = false;
  stageRef.value = null;
  score.value = 0;
  streak.value = 0;
  bestChain.value = 0;
  correct.value = 0;
  wrong.value = 0;
  verdict.value = { text: '', why: '', good: true };
}

useStopOnLeave(teardown);
useResetOnEnter(resetRun);

onUnmounted(() => { over.value = true; clearTimeout(advanceTimer); });
</script>
